import React, { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../../api";
import { Icon, Empty, Spinner } from "../../components/ui";
import { derive } from "../admin/recordDerive";

const STAGE_PROC = { SUBMISSION: "Submitter", END_PROCESS: "Uploader", MODIFICATION: "Modifier", MORE_INFO: "More Info" };
const pad = (n) => String(n).padStart(2, "0");
const hd = (t) => { try { if (!t) return "—"; const d = new Date(t); return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`; } catch { return "—"; } };

function parseRounds(record) {
  let rounds = [];
  try { rounds = record.workHistory ? JSON.parse(record.workHistory) : []; } catch { rounds = []; }
  return Array.isArray(rounds) ? rounds.map((h, i) => ({ ...h, _i: i })) : [];
}

function Round({ h, pinned }) {
  return (
    <div className={"jr-card" + (pinned ? " jr-pinned" : "")}>
      <div className="jr-head">
        <span className={"jr-stage" + (pinned ? " on" : "")}>{STAGE_PROC[h.stage] || h.stage || "—"}</span>
        {h.status && <span className="jr-status">{h.status}</span>}
        <span className="jr-spacer" />
        <span className="jr-by">{h.by || "A user"}</span>
        <span className="jr-when">{h.date || hd(h.dateISO)}</span>
      </div>
      <div className="jr-body">
        {h.inbox && <span><i>Inbox</i>{h.inbox}</span>}
        {h.requestNumber && <span><i>Request #</i>{h.requestNumber}</span>}
        {h.authNumber && <span><i>Auth #</i>{h.authNumber}</span>}
        {h.cpts && <span><i>CPTs</i>{h.cpts}</span>}
        {h.icds && <span><i>ICDs</i>{h.icds}</span>}
        {h.submitterGroup && <span><i>Group</i>{h.submitterGroup}</span>}
        {h.submitterName && <span><i>Submitter</i>{h.submitterName}</span>}
        {h.reason && <span><i>Reason</i>{h.reason}</span>}
        {h.remarks && <span><i>Remarks</i>{h.remarks}</span>}
        {h.issueType && <span><i>Issue</i>{h.issueType}{h.issueReason ? ` — ${h.issueReason}` : ""}</span>}
      </div>
    </div>
  );
}

function JourneyTrail({ record }) {
  const rounds = parseRounds(record);
  if (rounds.length === 0) return <div className="empty-mini">No process history recorded for this record yet.</div>;
  let approvalIdx = rounds.length - 1;
  for (let i = rounds.length - 1; i >= 0; i--) { if ((rounds[i].status || "").toLowerCase() === "approved") { approvalIdx = i; break; } }
  const top = rounds[approvalIdx];
  const rest = rounds.filter((h) => h._i !== approvalIdx).reverse();
  return (
    <div className="jr-wrap">
      {top && <><div className="jr-section-h">Approval / final</div><Round h={top} pinned /></>}
      {rest.length > 0 && <>
        <div className="jr-section-h">Earlier — the processes it went through</div>
        {rest.map((h) => <Round key={h._i} h={h} />)}
      </>}
    </div>
  );
}

export function RecordJourney() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try { setRows(await api.historicalRecords()); setErr(""); }
    catch (e) { setErr(e.message); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const results = useMemo(() => {
    const all = (rows || []).filter((r) => { try { const h = r.workHistory ? JSON.parse(r.workHistory) : []; return Array.isArray(h) && h.length > 0; } catch { return false; } });
    if (!q.trim()) return all.slice(0, 60);
    const s = q.toLowerCase();
    return all.filter((r) => { const d = derive(r) || {}; return [r.mrn, r.uniqueId, d.orders, r.orderName, r.workedByName, r.paStatus].some((v) => (v || "").toString().toLowerCase().includes(s)); }).slice(0, 60);
  }, [rows, q]);

  if (rows === null) return <div className="empty"><Spinner /></div>;

  return (
    <div className="jr-page">
      <div className="jr-head-bar">
        <div>
          <h2 style={{ margin: 0 }}>Record journey</h2>
          <div className="cell-sub">Search a record to see every process it went through on the way to approval.</div>
        </div>
        <div className="inv-search"><Icon.search width="15" height="15" /><input className="inp" placeholder="Search PID, order, UID, worker…" value={q} onChange={(e) => { setQ(e.target.value); setSel(null); }} />{q && <button className="ds-clear" onClick={() => setQ("")}><Icon.x width="13" height="13" /></button>}</div>
      </div>

      {err && <div className="import-err"><Icon.x width="15" height="15" /><span>{err}</span></div>}

      <div className="jr-split">
        <div className="jr-list card cmd-card">
          {results.length === 0
            ? <Empty title="No records" text="Try a different search." />
            : results.map((r) => { const d = derive(r) || {}; const rounds = parseRounds(r); return (
                <button key={r.id} className={"jr-list-item" + (sel && sel.id === r.id ? " on" : "")} onClick={() => setSel(r)}>
                  <div className="jr-li-top"><b className="mono">{r.mrn}</b><span className="jr-li-status">{r.paStatus || "—"}</span></div>
                  <div className="jr-li-order">{d.orders || r.orderName || "—"}</div>
                  <div className="jr-li-meta">{rounds.length} round(s) · last by {r.workedByName || r.assignedUserName || "—"}</div>
                </button>
              ); })}
        </div>
        <div className="jr-detail card cmd-card">
          {!sel ? <div className="empty-mini">Select a record on the left to see its journey.</div>
            : <>
                <div className="jr-detail-h"><b>PID {sel.mrn}</b> · {sel.orderName || (derive(sel) || {}).orders || ""}</div>
                <JourneyTrail record={sel} />
              </>}
        </div>
      </div>
    </div>
  );
}